import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Support-wide header counts.
  overview: { data: null, loading: false, error: null },
  // Every live case, longest wait first.
  liveQueue: { items: [], total: 0, page: 1, queue: 'general', loading: false, error: null },
  // The archive, live and past. Filters live here so a refetch reuses them.
  archive: {
    items: [],
    total: 0,
    page: 1,
    loading: false,
    error: null,
    filters: { q: '', filter: 'all', queue: 'general' },
  },
  // The archive reads a transcript through the agent surface; the console has none.
  transcript: { id: null, page: 1, total: 0, items: [], loading: false, error: null },
  deletingId: null,
  deleteError: null,
  // Load and seven days of history, already colour-banded by the server.
  agentsMetric: { items: [], loading: false, error: null },
  // A week of issue categories plus the totals row and at most one alert.
  issues: { data: null, loading: false, error: null },
};

const supportConsoleSlice = createSlice({
  name: 'supportConsole',
  initialState,
  reducers: {
    getSupportOverviewAction: (state) => {
      state.overview.loading = true;
      state.overview.error = null;
    },
    getSupportOverviewSuccess: (state, action) => {
      state.overview.data = action.payload;
      state.overview.loading = false;
    },
    getSupportOverviewFailure: (state, action) => {
      state.overview.loading = false;
      state.overview.error = action.payload;
    },

    setLiveQueueQueue: (state, action) => {
      state.liveQueue.queue = action.payload;
      state.liveQueue.page = 1;
      state.liveQueue.items = [];
      state.liveQueue.loading = true;
      state.liveQueue.error = null;
    },
    setLiveQueuePage: (state, action) => {
      state.liveQueue.page = action.payload;
      state.liveQueue.items = [];
      state.liveQueue.loading = true;
      state.liveQueue.error = null;
    },
    getLiveQueueAction: (state) => {
      state.liveQueue.loading = true;
      state.liveQueue.error = null;
    },
    getLiveQueueSuccess: (state, action) => {
      state.liveQueue.items = action.payload.items;
      state.liveQueue.total = action.payload.total;
      state.liveQueue.loading = false;
    },
    getLiveQueueFailure: (state, action) => {
      state.liveQueue.items = [];
      state.liveQueue.loading = false;
      state.liveQueue.error = action.payload;
    },

    // A new filter always restarts at page 1; keeping the old page shows an empty table.
    setArchiveFilters: (state, action) => {
      state.archive.filters = { ...state.archive.filters, ...action.payload };
      state.archive.page = 1;
      state.archive.loading = true;
      state.archive.error = null;
      state.archive.items = [];
    },
    setArchivePage: (state, action) => {
      state.archive.page = action.payload;
      state.archive.loading = true;
      state.archive.error = null;
      state.archive.items = [];
    },
    getSupportArchiveAction: (state) => {
      state.archive.loading = true;
      state.archive.error = null;
    },
    getSupportArchiveSuccess: (state, action) => {
      state.archive.items = action.payload.items;
      state.archive.total = action.payload.total;
      state.archive.loading = false;
    },
    getSupportArchiveFailure: (state, action) => {
      state.archive.items = [];
      state.archive.loading = false;
      state.archive.error = action.payload;
    },

    openArchiveThreadAction: (state, action) => {
      // Drop the old transcript first, or it reads under the new row's header.
      state.transcript = {
        id: action.payload,
        page: 1,
        total: 0,
        items: [],
        loading: Boolean(action.payload),
        error: null,
      };
    },
    setArchiveThreadPage: (state, action) => {
      state.transcript.page = action.payload;
      state.transcript.items = [];
      state.transcript.loading = true;
      state.transcript.error = null;
    },
    openArchiveThreadSuccess: (state, action) => {
      if (
        action.payload.id !== state.transcript.id ||
        action.payload.page !== state.transcript.page
      )
        return;
      state.transcript.items = action.payload.items;
      state.transcript.total = action.payload.total;
      state.transcript.loading = false;
    },
    openArchiveThreadFailure: (state, action) => {
      if (
        action.payload.id !== state.transcript.id ||
        action.payload.page !== state.transcript.page
      )
        return;
      state.transcript.loading = false;
      state.transcript.error = action.payload.error;
    },

    deleteArchiveConversationAction: (state, action) => {
      state.deletingId = action.payload;
      state.deleteError = null;
    },
    deleteArchiveConversationSuccess: (state, action) => {
      state.archive.items = state.archive.items.filter((row) => row.id !== action.payload);
      state.deletingId = null;
      state.archive.total = Math.max(0, state.archive.total - 1);
    },
    deleteArchiveConversationFailure: (state, action) => {
      state.deletingId = null;
      state.deleteError = action.payload;
    },

    getIssueTrendsAction: (state) => {
      state.issues.loading = true;
      state.issues.error = null;
    },
    getIssueTrendsSuccess: (state, action) => {
      state.issues.data = action.payload;
      state.issues.loading = false;
    },
    getIssueTrendsFailure: (state, action) => {
      state.issues.loading = false;
      state.issues.error = action.payload;
    },

    getAgentsMetricAction: (state) => {
      state.agentsMetric.loading = true;
      state.agentsMetric.error = null;
    },
    getAgentsMetricSuccess: (state, action) => {
      state.agentsMetric.items = action.payload;
      state.agentsMetric.loading = false;
    },
    getAgentsMetricFailure: (state, action) => {
      state.agentsMetric.items = [];
      state.agentsMetric.loading = false;
      state.agentsMetric.error = action.payload;
    },
  },
});

export const {
  setLiveQueuePage,
  setArchiveThreadPage,
  getLiveQueueAction,
  setLiveQueueQueue,
  getIssueTrendsAction,
  getIssueTrendsSuccess,
  getIssueTrendsFailure,
  openArchiveThreadAction,
  openArchiveThreadSuccess,
  openArchiveThreadFailure,
  deleteArchiveConversationAction,
  deleteArchiveConversationSuccess,
  deleteArchiveConversationFailure,
  setArchiveFilters,
  setArchivePage,
  getLiveQueueSuccess,
  getLiveQueueFailure,
  getAgentsMetricAction,
  getAgentsMetricSuccess,
  getAgentsMetricFailure,
  getSupportOverviewAction,
  getSupportArchiveAction,
  getSupportOverviewSuccess,
  getSupportOverviewFailure,
  getSupportArchiveSuccess,
  getSupportArchiveFailure,
} = supportConsoleSlice.actions;

export { supportConsoleSlice };
